import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Fetch, SequenceState, UseFf } from "../types";

// Alias
type AnyJson = Record<string, unknown>;

// DEFAULT: useState and useEffect
export const makeFf = <T extends AnyJson, U extends AnyJson>(f: (arg: T) => Promise<U>): UseFf<T, U> => {
  const ff = fmap(f);
  const useFf: UseFf<T, U> = props => {
    const $ = _c(9);
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        status: "loading"
      };
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    const [seqState, setSeqState] = useState(t0);
    const fetchNumber = useRef(0);
    let t1;
    if ($[1] !== props) {
      t1 = () => {
        const currentFetchNumber = fetchNumber.current = fetchNumber.current + 1;
        ff(props).then(newState => {
          fetchNumber.current === currentFetchNumber && setSeqState(newState);
        });
      };
      $[1] = props;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const refetch = t1;
    let t2;
    let t3;
    if ($[3] !== refetch) {
      t2 = () => {
        refetch();
      };
      t3 = [refetch];
      $[3] = refetch;
      $[4] = t2;
      $[5] = t3;
    } else {
      t2 = $[4];
      t3 = $[5];
    }
    useEffect(t2, t3);
    let t4;
    if ($[6] !== refetch || $[7] !== seqState) {
      t4 = [seqState, refetch];
      $[6] = refetch;
      $[7] = seqState;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  };
  return useFf;
};
type FetchFunctor<T extends AnyJson, U extends AnyJson> = (arg: T) => Promise<SequenceState<U>>;
// Convert `(props: T) => Promise<U>` to `(props: T) => Promise<SequenceState<U>>`
const fmap = <T extends AnyJson, U extends AnyJson>(f: Fetch<T, U>): FetchFunctor<T, U> => props => f(props).then(data => ({
  status: "ok" as const,
  data
})).catch(error => ({
  status: "ng" as const,
  error: error instanceof Error ? error : new Error(error.toString())
}));